import { Box, Chip, ChipProps } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { Business_Profile_Enum } from '@kheops/graphql';
import { ContractPreview } from '../../hooks/useContracts';
import { ServiceConfigurations } from '../services/state';
import { MinimumOrderValue } from '../../common/state/state';
import { getDeliveryZoneFromAdministrativeArea, getMinimumOrderValueFromDeliveryZones } from '../../common/utils/common.utils';
import MinimumOrderValueChip from '../../common/components/MinimumOrderValueChip';
import DeliveryTimeChip from '../../common/components/DeliveryTimeChip';
import CommissionInfo from '../../common/components/CommissionInfo';
import { currentContextAtom } from '../../state';
import { CompanyInfo } from './CompanyInfoCard';

export interface CompanyHeaderChipsProps {
  company: CompanyInfo;
  contract: ContractPreview;
  compact?: boolean;
}

export default function CompanyHeaderChips({ company, contract, compact }: CompanyHeaderChipsProps): React.JSX.Element {
  const { t } = useTranslation(['settings']);
  const { companyAddress, realm } = useAtomValue(currentContextAtom);
  const minimumOrderValue = useMemo((): MinimumOrderValue | undefined => {
    const movFromDeliveryZone = getMinimumOrderValueFromDeliveryZones(company.delivery_zones, companyAddress.administrativeAreaLevel2 || '');

    if (contract.minimum_order_value) {
      return {
        type: contract.minimum_order_value_type,
        value: contract.minimum_order_value,
        negotiable: movFromDeliveryZone?.negotiable,
      };
    }

    return movFromDeliveryZone;
  }, [contract, company, companyAddress]);

  const deliveryZone = useMemo(() => {
    return getDeliveryZoneFromAdministrativeArea(company.delivery_zones, companyAddress.administrativeAreaLevel2 || '');
  }, [company, companyAddress])

  const chipsProps = useMemo((): ChipProps[] => {
    return ServiceConfigurations.reduce((acc, { service, wordingNamespace, icon }) => {
      if (company[service]) {
        let label: string;

        switch (service) {
          case 'provide_return_policy':
            label = t(`settings:${wordingNamespace}.values.${company.return_policy!.toLocaleLowerCase()}`);

            break;
          default:
            label = t(`settings:${wordingNamespace}.chip_label`);
        }

        acc.push({
          icon: <img src={icon} />,
          label,
        });
      }

      return acc;
    }, [] as ChipProps[]);
  }, [company]);

  
  if (realm === Business_Profile_Enum.Supplier) {
    return (
      <Box 
        sx={{ 
          display: 'flex',
          '& .MuiChip-root': {
          backgroundColor: compact ? 'surfaceContainerLow.main' : 'surfaceContainerHigh.main',
          }
        }}>
        {!!contract.supplier_commission_rate && (
          <CommissionInfo short commission={contract.supplier_commission_rate} />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      <MinimumOrderValueChip
        variant="outlinedTag"
        minimumOrderValue={minimumOrderValue?.value}
        minimumOrderValueType={minimumOrderValue?.type}
        negotiable={minimumOrderValue?.negotiable}
      />
      {deliveryZone?.delivery_time && <DeliveryTimeChip deliveryTime={deliveryZone.delivery_time} variant="outlinedTag" />}
      {!compact && chipsProps.map((props) => (
        <Chip {...props} variant="outlinedTag" key={props.label as string} />
      ))}
    </Box>
  );
}
